import type {MockComplexityConfig} from "../../types";

const IT_ENVIRONMENT_COMPLEXITY: MockComplexityConfig = {
  tiers: [
    {name: "Non Complex", colorPalette: "green", lowerBound: null, upperBound: 1000},
    {name: "Complex", colorPalette: "red", lowerBound: 1000, upperBound: null},
  ],
  attributes: [
    {
      id: "ca-num-apps",
      name: "Number of Applications in Use",
      valueType: "Integer",
      weight: 3,
      operator: "First",
      initialValue: 1,
      sourceCount: 1,
      defaultScore: 100,
      rules: [
        {conditions: [{label: "≤ 2", evaluate: v => typeof v === "number" && v <= 2}], score: 0},
        {conditions: [{label: "> 2 and ≤ 5", evaluate: v => typeof v === "number" && v > 2 && v <= 5}], score: 50},
        {conditions: [{label: "> 5", evaluate: v => typeof v === "number" && v > 5}], score: 100},
      ],
    },
    {
      id: "ca-it-staff",
      name: "IT Staff Competence",
      valueType: "StringSet",
      weight: 2,
      operator: "First",
      initialValue: ["Dedicated In-House IT Department"],
      options: [
        "Dedicated In-House IT Department",
        "Outsourced IT Support",
        "Part-time / Shared IT Resources",
        "No Formal IT Staff",
      ],
      sourceCount: 2,
      defaultScore: 0,
      rules: [
        {
          conditions: [
            {
              label: 'contains "Dedicated In-House IT Department"',
              evaluate: v => Array.isArray(v) && v.includes("Dedicated In-House IT Department"),
            },
          ],
          score: 75,
        },
        {
          conditions: [
            {
              label: 'contains "Outsourced IT Support"',
              evaluate: v => Array.isArray(v) && v.includes("Outsourced IT Support"),
            },
          ],
          score: 50,
        },
        {
          conditions: [
            {
              label: 'contains "No Formal IT Staff"',
              evaluate: v => Array.isArray(v) && v.includes("No Formal IT Staff"),
            },
          ],
          score: 25,
        },
      ],
    },
    {
      id: "ca-sig-it-changes",
      name: "Significant IT Changes in Year",
      valueType: "Boolean",
      weight: 3,
      operator: "First",
      initialValue: true,
      sourceCount: 1,
      defaultScore: 0,
      rules: [
        {conditions: [{label: "equals Yes", evaluate: v => v === true}], score: 150},
        {conditions: [{label: "equals No", evaluate: v => v === false}], score: 0},
      ],
    },
    {
      id: "ca-custom-software",
      name: "Use of Custom or Source-Modified Software",
      valueType: "StringSet",
      weight: 5,
      operator: "First",
      initialValue: ["Minor customisation"],
      options: ["No customisation", "Minor customisation", "Major customisation", "Fully custom-built"],
      sourceCount: 1,
      defaultScore: 0,
      rules: [
        {
          conditions: [
            {
              label: 'contains "Minor customisation"',
              evaluate: v => Array.isArray(v) && v.includes("Minor customisation"),
            },
          ],
          score: 100,
        },
        {
          conditions: [
            {
              label: 'contains "Major customisation"',
              evaluate: v => Array.isArray(v) && v.includes("Major customisation"),
            },
          ],
          score: 150,
        },
        {
          conditions: [
            {
              label: 'contains "Fully custom-built"',
              evaluate: v => Array.isArray(v) && v.includes("Fully custom-built"),
            },
          ],
          score: 200,
        },
        {
          conditions: [
            {label: 'contains "No customisation"', evaluate: v => Array.isArray(v) && v.includes("No customisation")},
          ],
          score: 0,
        },
      ],
    },
    {
      id: "ca-erp",
      name: "Use of ERP",
      valueType: "Boolean",
      weight: 3,
      operator: "First",
      initialValue: true,
      sourceCount: 1,
      defaultScore: 0,
      rules: [
        {conditions: [{label: "equals Yes", evaluate: v => v === true}], score: 100},
        {conditions: [{label: "equals No", evaluate: v => v === false}], score: 0},
      ],
    },
    {
      id: "ca-dataflow",
      name: "Dataflow Automation Level",
      valueType: "StringSet",
      weight: 3,
      operator: "Max",
      initialValue: ["Highly Automated", "Semi-Automated", "Manual"],
      options: ["Highly Automated", "Semi-Automated", "Manual"],
      sourceCount: 2,
      defaultScore: 0,
      rules: [
        {
          conditions: [
            {label: 'contains "Highly Automated"', evaluate: v => Array.isArray(v) && v.includes("Highly Automated")},
          ],
          score: 100,
        },
        {
          conditions: [
            {label: 'contains "Semi-Automated"', evaluate: v => Array.isArray(v) && v.includes("Semi-Automated")},
          ],
          score: 50,
        },
        {conditions: [{label: 'contains "Manual"', evaluate: v => Array.isArray(v) && v.includes("Manual")}], score: 0},
      ],
    },
    {
      id: "ca-db-access",
      name: "Direct Database/Data Access Possible",
      valueType: "Boolean",
      weight: 3,
      operator: "First",
      initialValue: true,
      sourceCount: 1,
      defaultScore: 0,
      rules: [
        {conditions: [{label: "equals Yes", evaluate: v => v === true}], score: 100},
        {conditions: [{label: "equals No", evaluate: v => v === false}], score: 0},
      ],
    },
    {
      id: "ca-num-interfaces",
      name: "Num Automated Interfaces/Integrations",
      valueType: "Integer",
      weight: 3,
      operator: "First",
      initialValue: 1,
      sourceCount: 1,
      defaultScore: 0,
      rules: [
        {conditions: [{label: "= 0", evaluate: v => typeof v === "number" && v === 0}], score: 0},
        {conditions: [{label: "> 0 and ≤ 3", evaluate: v => typeof v === "number" && v > 0 && v <= 3}], score: 50},
        {conditions: [{label: "> 3", evaluate: v => typeof v === "number" && v > 3}], score: 100},
      ],
    },
    {
      id: "ca-ai-tech",
      name: "AI / Emerging Technology Usage",
      valueType: "Boolean",
      weight: 3,
      operator: "First",
      initialValue: false,
      sourceCount: 1,
      defaultScore: 0,
      rules: [
        {conditions: [{label: "equals Yes", evaluate: v => v === true}], score: 100},
        {conditions: [{label: "equals No", evaluate: v => v === false}], score: 0},
      ],
    },
  ],
};

export const ELC_SCORING_CONFIGS: Record<string, MockComplexityConfig> = {
  "elc-it-environment": IT_ENVIRONMENT_COMPLEXITY,
};
